import ProductMeta from './meta';
import { CircularProgress, Container, Grid } from "@material-ui/core";
import { useContext, useEffect, useState } from "react";
import { TrackingContext } from "../../providers/tracking";
import { ImageGalleryIndexContext } from "../../providers/imageGalleryIndex"
import { makeStyles } from '@material-ui/styles';

// Elements
import ProductInfo from "./info";
import ProductPrice from "./price";
import ProductBuy from "./buy";
import Additional from "./additional";
import parsePrices from "functions/parseSWPrices";
import LazyHydrate from "tools/lazyHydrate";
import {useRouter} from "next/router";
import Breadcrumbs from "../../breadcrumbs";
import ProductGallery from "./gallery";
import StickyBuy from "./stickyBuy";
import RelatedContent from "./relatedContent";
import ElevateRelated from "./recommendations/elevateRelated";
import ProductJsonLD from "./jsonLd";
import ProductLabels from './partial/labels';
import CrossSell from '@components/page/product/recommendations/swCrossSell';
import ProductConfigurator from '@components/page/product/configurator';

const useStyles = makeStyles(theme => ({
	main: {
		backgroundColor: theme.palette.background,
		[theme.breakpoints.up('md')]: {
			paddingTop: 30
		},
		[theme.breakpoints.down('lg')]: {
			paddingLeft: 15,
			paddingRight: 15
		}
	},
	isLoading: {
		opacity: 0.75
	},
	loader: {
		position: 'fixed',
		left: '50%',
		top: '50%',
		marginLeft: -20,
		zIndex: 1000
	},
	section__wrapper: {
		[theme.breakpoints.up('sm')]: {
			padding: '0 0 20px'
		}
	},
	info__wrapper: {
		display: 'contents',
		[theme.breakpoints.up('sm')]: {
			display: 'flex',
			flexDirection: 'column',
			width: 'calc(40% - 15px)'
		}
	},
	gallery__wrapper: {
		position: 'relative',
		width: 'calc(100% + 30px)',
		margin: '0 -15px 15px',
		[theme.breakpoints.up('sm')]: {
			width: 'calc(60% - 15px)',
			margin: 0
		}
	},
	breadcrumbs: {
		'& a': {
			fontSize: '14px !important',
			fontWeight: '300 !important'
		}
	},
	breadcrumbsDesktop: {
		display: 'none',
		[theme.breakpoints.up('sm')]: {
			display: 'flex',
			padding: '0 0 5px'
		}
	},
	breadcrumbsMobile: {
		display: 'none',
		[theme.breakpoints.down('sm')]: {
			display: 'flex',
			marginTop: 0,
			padding: 0,
			width: '100%',
			'& ul': {
				marginTop: 0,
				justifyContent: 'flex-start',
			},
		}
	},
	description: {
		margin: 0,
		fontSize: 15,
		fontWeight: 300,
		lineHeight: 1.5,
		letterSpacing: 0,
		color: theme.palette.primary.color,
        width: '100%',
		[theme.breakpoints.down('sm')]: {
			fontSize: 14
		},
        '& p': {
            marginBottom: 5,
        }
	}
}), {name: 'ProductPage'});



export default function ProductPage({product, breadcrumb, configurator, isLoading, canonicalPathInfo, productFamily, appData: {meta, menu}}) {
	const classes = useStyles();
	const [state, setState] = useState({
		isLoading: isLoading
	});

	const router = useRouter();
	const prices = parsePrices(product.calculatedPrices && product.calculatedPrices.length > 0 ? product.calculatedPrices[0] : product.calculatedPrice);
	const {trackingInitiated, actions} = useContext(TrackingContext);
	const { media } = product;
	const { imageGalleryIndex } = useContext(ImageGalleryIndexContext);

	useEffect(() => {
		if(product && !isLoading && trackingInitiated) {
			actions.pageImpression(product.metaTitle || product.translated.name, 'product_details_page');
			detailsTracking();
		}
	}, [router.asPath, isLoading, trackingInitiated]);

	useEffect(() => {
		setState({...state, isLoading: false});
	}, [router, isLoading]);

	function productIsAvailable() {
		return product.available;
	}

	function detailsTracking() {
		let price = product.calculatedPrices && product.calculatedPrices.length > 0 ? product.calculatedPrices[0] : product.calculatedPrice;

		actions.productDetails({
			id: product.productNumber,
			price: price.unitPrice,
			discount: Math.abs(prices?.discount?.amount),
			brand: product.manufacturer ? product.manufacturer.translated.name : '',
			list: {id: '',  name:'product_detail_page', location: router.asPath},
			analytics: product?.extensions?.newport?.analytics
		});
	}

	function getShortDescription() {
        const shortDesc = product?.translated?.customFields?.short_description
        if (shortDesc?.length && shortDesc?.length > 10) {
            return shortDesc
        }

        const text = product?.translated?.description
		if (text) {
			const sentences = text.match(/[^.!?]+[.!?]+[\])'"`’”]*\s*/g);
			if (!sentences) return text;
			return sentences.slice(0, 1).join('').trim();
		}

        return ''
	}

	return (
        <>
            <ProductMeta product={product} prices={prices} canonicalPath={canonicalPathInfo} />
            <ProductJsonLD
                product={product}
                media={media}
                breadcrumb={breadcrumb}
                meta={meta}
                prices={prices}
                canonicalPath={canonicalPathInfo}
            />

            <div className={[isLoading || state.isLoading ? classes.isLoading : ''].join(' ')} style={{ flexGrow: 1 }}>
                {isLoading || state.isLoading ? <CircularProgress className={classes.loader} /> : ''}
                <div className={classes.main}>
                    <Container disableGutters={true}>
                        <Breadcrumbs
                            breadcrumbs={breadcrumb}
                            product={true}
                            noGutters={true}
                            className={[classes.breadcrumbsDesktop, classes.breadcrumbs].join(' ')}
                        />
                        <Grid
                            className={classes.section__wrapper}
                            container
                            style={{ justifyContent: 'space-between' }}
                        >
                            <Grid className={classes.gallery__wrapper} item xs={false} sm="auto">
                                {product.media && product.media.length > 0 ? (
                                    <ProductGallery
                                        coverId={product.cover ? product.cover.id : false}
                                        media={media}
                                        product={product}
                                        key={product.id}
                                    />
                                ) : (
                                    ''
                                )}

                                <ProductLabels
                                    available={productIsAvailable()}
                                    large={true}
                                    prices={prices}
                                    streamIds={product?.streamIds}
                                    propertyIds={product?.propertyIds}
                                ></ProductLabels>
                            </Grid>

                            <Grid className={classes.info__wrapper} item xs={12} sm="auto">
                                <Breadcrumbs
                                    breadcrumbs={breadcrumb}
                                    product={true}
                                    noGutters={true}
                                    className={[classes.breadcrumbsMobile, classes.breadcrumbs].join(' ')}
                                />
                                <ProductInfo product={product} breadcrumbs={breadcrumb} />
                                <ProductPrice product={product} />
                                <div
                                    className={classes.description}
                                    dangerouslySetInnerHTML={{ __html: getShortDescription() }}
                                ></div>
                                {configurator.length > 0 && (
                                    <ProductConfigurator
                                        product={product}
                                        configurator={configurator}
                                        router={router}
                                    />
                                )}
                                <ProductBuy
                                    product={product}
                                    available={productIsAvailable()}
                                    breadcrumbs={breadcrumb}
                                    stock={product.availableStock}
                                    productFamilyArticle={productFamily}
                                />

                                <CrossSell productId={product.id} />
                            </Grid>

                            <Grid item>
                                <Additional product={product} menu={menu} productFamilyArticle={productFamily} />
                            </Grid>

                            {productIsAvailable() ? (
                                <StickyBuy
                                    product={product}
                                    breadcrumbs={breadcrumb}
                                    available={productIsAvailable()}
                                />
                            ) : (
                                ''
                            )}

                            <Grid xs={12} item>
                                <RelatedContent product={product} productFamilyArticle={productFamily} />
                            </Grid>
                        </Grid>
                    </Container>
                </div>

                <LazyHydrate id={'ProductPage-' + product.id} on="visible">
                    <Container disableGutters>
                        <ElevateRelated product={product} configurator={configurator} breadcrumbs={breadcrumb} />
                    </Container>
                </LazyHydrate>
            </div>
        </>
    );
}
